import React, { useCallback } from 'react'

// Constants
import { useFlag } from 'constants/flags'

// Utils
import { ErrorHandler } from 'modules/tracing'
import { getMemberEmail } from 'modules/workspace/utils'

// Hooks
import { useMembershipsPanel } from './hooks'
import useUserProfile from 'modules/user/useUserProfile'

// Components
import Table, { TableComponents } from 'components/Table'
import Button from 'components/Button'
import ErrorMessage from 'components/ErrorMessage'
import MemberBadge, {
  BadgeType,
} from 'modules/workspace/components/MemberBadge/MemberBadge'

// Local Components
import RoleTabs from './components/RoleTabs'
import MembersTableLoading from './components/MembersTableLoading'

// Styles
import {
  EmptyList,
  ErrorContainer,
  Filter,
  LoadMoreWrapper,
  MemberCard,
  StyledTableCell,
  TableWrapper,
} from './MembershipsPanel.styles'

// Types
import { WorkspaceMembershipFragment } from 'gqlTypes'

interface MembershipsPanelProps {
  workspaceId: string
  renderSecondaryActions?: (
    member: WorkspaceMembershipFragment
  ) => React.ReactNode
}

// Helpers
const getRoleText = (
  userRole: WorkspaceMembershipFragment['role'],
  isEditor: boolean
) => {
  if (userRole === 'GUEST') {
    return 'Guest'
  } else if (isEditor === true) {
    return 'Editor'
  } else {
    return 'Viewer'
  }
}

/**
 * MembershipsPanel
 *
 * Renders the table of members in the Workspace settings page
 */
export const MembershipsPanel: React.FC<MembershipsPanelProps> = ({
  workspaceId,
  renderSecondaryActions,
}) => {
  // TODO: Remove the FF when its released to production
  // https://github.com/sketch-hq/Cloud/issues/9667
  const isWorkspaceMembersFilterOn = useFlag('filter-workspace-members')
  const { data: userProfile } = useUserProfile()

  const {
    loading,
    error,
    data,
    fetchMore,
    userCount,
    filters,
    previousNumberOfMemberRows,
  } = useMembershipsPanel(workspaceId)

  const currentUserId = userProfile?.me.identifier
  const isSuperadmin = !!userProfile?.me.isSuperadmin

  // Render each row in the members table
  const renderMemberRow = useCallback(
    member => {
      // Builds an array of badges for each member in the memberships table
      const getBadges = (member: WorkspaceMembershipFragment) => {
        const badges: BadgeType[] = []

        if (member.user?.identifier === currentUserId && isSuperadmin) {
          badges.push('super-admin')
        }

        if (member.isOwner) {
          badges.push('owner')
        }

        if (member.role === 'ADMIN' && !member.isOwner) {
          badges.push('admin')
        }

        if (member.role === 'FINANCE') {
          badges.push('finance')
        }

        if (!member.acceptedAt) {
          badges.push('pending-invite')
        }

        return badges
      }

      return (
        <TableComponents.TableRow data-testid="members-table-row">
          <StyledTableCell>
            <MemberCard
              name={member?.user?.name}
              email={getMemberEmail(member)!}
              avatarSrc={member?.user?.avatar?.small}
              $dimmed={!member.acceptedAt}
            />
          </StyledTableCell>
          <StyledTableCell data-testid="member-access">
            <span>{getRoleText(member.role, member.isEditor)}</span>
            {getBadges(member).map(badge => (
              <MemberBadge key={badge} badge={badge} />
            ))}
          </StyledTableCell>
          {renderSecondaryActions && (
            <StyledTableCell>{renderSecondaryActions(member)}</StyledTableCell>
          )}
        </TableComponents.TableRow>
      )
    },
    [renderSecondaryActions, currentUserId, isSuperadmin]
  )

  /* Render a Skeleton table for better loading experience */
  let children = (
    <MembersTableLoading items={previousNumberOfMemberRows.current} />
  )

  if (error) {
    children = (
      <ErrorContainer>
        <ErrorMessage.Generic />
      </ErrorContainer>
    )
  }

  if (data) {
    const {
      userCanAccess,
      list,
      totalMemberships,
      totalGuests,
    } = data.workspace.memberships

    if (!userCanAccess) {
      // TODO: find a better !userCanAccess handling strategy
      // see: https://github.com/sketch-hq/Cloud/issues/7500
      return null
    }

    if (!list || !totalMemberships) {
      ErrorHandler.shouldNeverHappen(
        'memberships should contain a list if userCanAccess is true'
      )
      return null
    }

    const { entries: memberships } = list
    const { totalCount: listCount } = list.meta
    const { totalCount: totalCountUsers } = totalMemberships.meta
    const { totalCount: totalCountGuests } = totalGuests?.meta || {
      totalCount: 0,
    }

    /*
      We are only updating the total counts if they differ
      from the previous value. This will avoid always rendering
      the Skeleton UI in the filter tabs when changing filter tabs.
    */
    if (userCount?.current?.totalMembers !== totalCountUsers) {
      userCount.current.totalMembers = totalCountUsers
    }

    if (userCount?.current?.totalGuests !== totalCountGuests) {
      userCount.current.totalGuests = totalCountGuests
    }

    // Table has an hidden header, however, it still has
    // content due to accessibility reasons.
    const tableHeader = [{ label: 'Name' }, { label: 'Role' }]
    if (renderSecondaryActions) {
      tableHeader.push({ label: 'Actions' })
    }

    /* Renders the members table */
    children = (
      <>
        {!memberships.length ? (
          <EmptyList>No Members found.</EmptyList>
        ) : (
          <TableWrapper>
            <Table
              header={tableHeader}
              items={memberships}
              renderItem={renderMemberRow}
              hideHeader
            />
          </TableWrapper>
        )}

        {/** Pagination: Load More button */}
        {listCount > memberships.length ? (
          <LoadMoreWrapper>
            <Button
              variant="secondary-untinted"
              small
              onClick={fetchMore}
              loading={loading}>
              Load More
            </Button>
          </LoadMoreWrapper>
        ) : null}
      </>
    )
  }

  return (
    <>
      {isWorkspaceMembersFilterOn && (
        <RoleTabs filters={filters} userCount={userCount} />
      )}
      <Filter
        data-test-id="search-filter"
        name="search-filter"
        placeholder="Search for Members by name or email"
        isWorkspaceMembersFilterOn={isWorkspaceMembersFilterOn}
        onChange={filters.setFilter}
        value={filters.filter}
      />
      {children}
    </>
  )
}
